import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import {
  MessagesService,
  Message,
  Conversation,
} from '../services/messages.service';
import { WebSocketService } from '../services/websocket.service';
import { AuthService, User } from '../services/auth.service';

@Component({
  selector: 'app-conversation',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './conversation.component.html',
  styleUrl: './conversation.component.css',
})
export class ConversationComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;

  conversation: Conversation | null = null;
  messages: Message[] = [];
  newMessage = '';
  loading = true;
  loadingMessages = false;
  sendingMessage = false;
  error: string | null = null;
  currentUser: User | null = null;
  conversationId: string = '';

  connectionState: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  onlineUsers = new Set<string>();

  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = false;
  private wasUserScrolled = false;
  private lastMessageCount = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private messagesService: MessagesService,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
      });

    this.webSocketService
      .getConnectionState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.connectionState = state;
      });

    this.messagesService.onlineUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => {
        this.onlineUsers = users;
      });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.conversationId = params['id'];
      if (this.conversationId) {
        this.loadConversation();
        this.loadMessages();
        this.setupRealtimeListeners();
        this.messagesService.joinConversation(this.conversationId);
      } else {
        this.router.navigate(['/messages']);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.conversationId) {
      this.messagesService.leaveConversation(this.conversationId);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom && !this.wasUserScrolled) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private setupRealtimeListeners(): void {
    this.messagesService
      .getConversationMessageStream(this.conversationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((messages) => {
        const previousLength = this.lastMessageCount;
        this.messages = messages;
        this.lastMessageCount = messages.length;

        if (messages.length > previousLength) {
          this.shouldScrollToBottom = true;

          if (document.visibilityState === 'visible') {
            setTimeout(() => this.markAsRead(), 500);
          }
        }
      });

    if (this.messagesContainer) {
      fromEvent(this.messagesContainer.nativeElement, 'scroll')
        .pipe(takeUntil(this.destroy$), debounceTime(100))
        .subscribe(() => this.checkIfUserScrolled());
    }

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this.markAsRead();
        }
      });
  }

  private checkIfUserScrolled(): void {
    if (!this.messagesContainer) return;

    const container = this.messagesContainer.nativeElement;
    const threshold = 100;

    this.wasUserScrolled =
      container.scrollTop <
      container.scrollHeight - container.clientHeight - threshold;
  }

  goToListing(listing: any): void {
    if (!listing?.id || !listing?.subcategory) return;
    this.router.navigate(['/listing-details', listing.subcategory, listing.id]);
  }

  loadConversation(): void {
    this.messagesService.getConversationDetails(this.conversationId).subscribe({
      next: (response) => {
        this.conversation = response.conversation;
        setTimeout(() => this.markAsRead(), 1000);
      },
      error: (error) => {
        this.error = error.message || 'Failed to load conversation';
        this.loading = false;
      },
    });
  }

  loadMessages(): void {
    this.loadingMessages = true;
    this.messagesService
      .getConversationMessages(this.conversationId)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.loadingMessages = false;
          this.lastMessageCount = response.messages.length;
          this.shouldScrollToBottom = true;
        },
        error: (error) => {
          this.error = error.message || 'Failed to load messages';
          this.loading = false;
          this.loadingMessages = false;
        },
      });
  }

  sendMessage(): void {
    const messageText = this.newMessage.trim();

    if (!messageText || this.sendingMessage || !this.conversation) {
      return;
    }

    if (!this.isConnectionHealthy()) {
      alert('Cannot send message. Connection lost. Please retry.');
      return;
    }

    const receiverId = this.conversation.other_user_id;

    this.newMessage = '';
    this.resetTextareaHeight();
    this.sendingMessage = true;

    this.messagesService
      .sendMessage(this.conversationId, {
        message: messageText,
        receiverId: receiverId,
      })
      .subscribe({
        next: () => {
          this.shouldScrollToBottom = true;
          this.sendingMessage = false;
          this.wasUserScrolled = false;

          setTimeout(() => {
            this.messageInput?.nativeElement?.focus();
          }, 100);
        },
        error: (error) => {
          this.sendingMessage = false;
          alert(error.message || 'Failed to send message. Please try again.');
        },
      });
  }

  markAsRead(): void {
    if (!this.conversation || this.messages.length === 0) return;

    const hasUnread = this.messages.some(
      (m) =>
        !m.is_read &&
        m.sender_id !== this.currentUser?.id?.toString() &&
        !m.isPending
    );

    if (!hasUnread) return;

    this.messagesService.markMessagesAsRead(this.conversationId).subscribe({
      error: (error) => console.error('Error marking as read:', error),
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onInputChange(): void {
    this.autoResizeTextarea();
  }

  private autoResizeTextarea(): void {
    if (!this.messageInput) return;

    const textarea = this.messageInput.nativeElement;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 128);
    textarea.style.height = newHeight + 'px';
  }

  private resetTextareaHeight(): void {
    if (!this.messageInput) return;
    this.messageInput.nativeElement.style.height = 'auto';
  }

  isMyMessage(message: Message): boolean {
    return !!(
      this.currentUser && message.sender_id === this.currentUser.id.toString()
    );
  }

  getMessageTime(message: Message): string {
    const date = new Date(message.created_at);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  getMessageDate(message: Message): string {
    const date = new Date(message.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  }

  shouldShowDateSeparator(index: number): boolean {
    if (index === 0) return true;

    const currentMessage = this.messages[index];
    const previousMessage = this.messages[index - 1];

    const currentDate = new Date(currentMessage.created_at).toDateString();
    const previousDate = new Date(previousMessage.created_at).toDateString();

    return currentDate !== previousDate;
  }

  goBack(): void {
    this.router.navigate(['/messages']);
  }

  getOtherUserName(): string {
    return this.conversation?.other_user_name || 'Unknown User';
  }

  getListingTitle(): string {
    return this.conversation?.listing?.title || 'Unknown Listing';
  }

  formatPrice(price: number | undefined): string {
    if (price === undefined) {
      return 'Price not available';
    }
    return this.messagesService.formatPrice(price);
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const container = this.messagesContainer.nativeElement;
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  trackByMessage(index: number, message: Message): string {
    return message.tempId || message.id;
  }

  retry(): void {
    this.error = null;
    this.loading = true;
    this.loadConversation();
    this.loadMessages();
  }

  canSendMessage(): boolean {
    return (
      !this.sendingMessage &&
      this.newMessage.trim().length > 0 &&
      this.newMessage.trim().length <= 1000 &&
      this.isConnectionHealthy()
    );
  }

  getPlaceholderText(): string {
    if (!this.isConnectionHealthy()) {
      return 'Reconnecting...';
    }
    if (this.sendingMessage) {
      return 'Sending...';
    }
    return `Message ${this.getOtherUserName()}...`;
  }

  isOtherUserOnline(): boolean {
    if (!this.conversation?.other_user_id) return false;
    return this.messagesService.isUserOnline(this.conversation.other_user_id);
  }

  getConnectionStatusText(): string {
    switch (this.connectionState) {
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Disconnected';
      default:
        return '';
    }
  }

  isConnectionHealthy(): boolean {
    return this.connectionState === 'connected';
  }

  retryConnection(): void {
    this.webSocketService.retry();
  }

  getMessageStatus(
    message: Message
  ): 'sent' | 'delivered' | 'read' | 'sending' | 'failed' {
    if (message.sendFailed) return 'failed';
    if (message.isPending) return 'sending';
    if (message.is_read) return 'read';
    return 'delivered';
  }

  retryFailedMessage(message: Message): void {
    if (!message.tempId || !this.conversation) return;

    this.messagesService.removeFailedMessage(
      this.conversationId,
      message.tempId
    );

    this.messagesService
      .sendMessage(this.conversationId, {
        message: message.message_text,
        receiverId: this.conversation.other_user_id,
      })
      .subscribe({
        error: (error) => {
          alert(error.message || 'Failed to send message');
        },
      });
  }

  removeFailedMessage(message: Message): void {
    if (message.tempId) {
      this.messagesService.removeFailedMessage(
        this.conversationId,
        message.tempId
      );
    }
  }
}
